import React, { useState } from 'react';
import { 
    useTheme,
    useMediaQuery,
    Container, 
    Box,
    Typography,
    IconButton,
    Badge,
    Grid,
} from '@mui/material';
import {
    usePather,
    withPather, 
} from 'react-pather';
import { 
    useParams, 
    useNavigate, 
    Navigate,
} from 'react-router-dom';
import { MenuRounded } from '@mui/icons-material';
import settingsTabs from './settingsTabs';
import SettingsDrawer from './SettingsDrawer';
import { 
    roleIcon, 
    isAdmin,
} from '../../../utils';
import { 
    getTabId, 
    getTabSlug,
} from './utils';

const TabLabels = ({ value, setValue, isExpandedMatch }) => {
    const theme = useTheme();
    const tabs = settingsTabs.filter(tab => isAdmin() ? true : !tab?.adminOnly);
    return (
        <Box>
            {
                tabs.map(current => {
                    return (
                        <Box 
                            key={current.id}
                            sx={{ 
                                display: 'flex', alignItems: 'center', 
                                justifyContent: 'flex-start', gap: 4, pl: 1, py: 1.5,
                                cursor: 'pointer', 
                                color: current.id === value && theme.palette.text.main,
                                backgroundColor: current.id === value && theme.palette.action.hover,
                                border: current.id === value && theme.palette.tinyBorder,
                                '&:hover': {
                                    border: theme.palette.tinyBorder,
                                    backgroundColor: theme.palette.action.hover,
                                },
                                m: 1,
                                borderRadius: '15px',
                            }}
                            onClick={() => setValue(current.id)}
                        >
                            {
                                <Box sx={{alignSelf: 'center', minWidth: !isExpandedMatch && '40px'}}>
                                    {   
                                        current?.adminOnly ?
                                        <>
                                            <Badge badgeContent={
                                                React.cloneElement(
                                                    roleIcon('admin'),
                                                    { 
                                                        width: "14", 
                                                        height: "14",  
                                                    }
                                                )
                                            }>
                                                {
                                                    React.cloneElement(
                                                        current.icon,
                                                        { 
                                                            style: {
                                                                fontSize: 28
                                                            } 
                                                        }
                                                    )
                                                }
                                            </Badge>
                                        </>:
                                        <>
                                            {
                                                React.cloneElement(
                                                    current.icon,
                                                    { 
                                                        style: {
                                                            fontSize: 28
                                                        } 
                                                    }
                                                )
                                            }
                                        </>
                                    }
                                </Box>
                            }
                            {   
                                isExpandedMatch &&
                                <Box>
                                    <Typography 
                                        sx={{
                                            width: '100%',
                                            fontWeight: current.id === value && 'bold',
                                        }} 
                                        valiant="h1" 
                                        component="div"
                                    >
                                        {current.label}
                                    </Typography>
                                </Box>
                            }
                        </Box>
                    );
                })
            }
        </Box>
    );
}

const Tabs = ({ value, toggleDrawer }) => {

    const theme = useTheme();
    const pather = usePather();
    const isExpandedMatch = useMediaQuery(theme.breakpoints.up(1080));

    const tabs = settingsTabs.filter(tab => {
        return isAdmin() ? true : !tab?.adminOnly;
    });
    const tab = tabs.find(tab => { return tab.id === value });

    if (!tab) {
        return <Navigate to={pather.reverse(pather.front.settings, { slug: getTabSlug(0) })} />;
    }

    return (
        <Box sx={{width: '100%', minHeight: '480px', borderLeft: isExpandedMatch && '0.3px solid #AAB4BE'}}>
            {       
                <Box>  
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1}}>
                        {   
                            !tab?.disableTitleIntegration &&
                            <Box sx={{ pt: 2, display: 'flex', alignItems: 'center'}}>
                                {
                                    !isExpandedMatch &&
                                    <Box sx={{pl: 2}}>
                                        <IconButton onClick={toggleDrawer(true)}>
                                            <MenuRounded fontSize='large'/>
                                        </IconButton>
                                    </Box>
                                }
                                <Box sx={{width: '100%', display: 'flex', justifyContent: 'center'}}>
                                    <h1>
                                        { tab.label }
                                    </h1>
                                </Box>
                            </Box>
                        }
                        <Box sx={{p: !tab?.disableTitleIntegration && 2, pt: !tab?.disableTitleIntegration && 0}}>
                            { 
                                React.cloneElement(
                                    tab.component,
                                    { 
                                        toggleDrawer,
                                        isDrawerExpandedMatch: isExpandedMatch 
                                    }
                                )
                            }
                        </Box>
                    </Box>
                </Box>
            }
        </Box>
    );
}

const Settings = ({ pather }) => {

    const theme = useTheme();
    const navigate = useNavigate();
    const { slug } = useParams();

    const isExpandedMatch = useMediaQuery(theme.breakpoints.up(1080));

    const [openSettingsDrawer, setOpenSettingsDrawer] = useState(false);

    const toggleDrawer = open => event => {
        if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) return;
        setOpenSettingsDrawer(open);
    };

    const goTo = id => navigate(pather.reverse(pather.front.settings, { slug: getTabSlug(id) }));

    let settingsPage = getTabId(slug);
    if (settingsPage === null || settingsPage === undefined) {
        return <Navigate to={pather.reverse(pather.front.settings, { slug: getTabSlug(0) })} />;
    }
    
    return (
        <Container sx={{height: '100%', mb: 10}}>
            <Box sx={{ flexGrow: 1 }}>
                { 
                    isExpandedMatch ?
                    <Grid container>
                        <Grid item xs={2}>
                            <TabLabels 
                                value={settingsPage}
                                setValue={goTo}
                                isExpandedMatch={isExpandedMatch}
                            />
                        </Grid>
                        <Grid item xs={10}>
                            <Tabs value={settingsPage} />
                        </Grid>
                    </Grid>:
                    <Tabs 
                        value={settingsPage} 
                        toggleDrawer={toggleDrawer}
                    /> 
                }
            </Box>
            <SettingsDrawer 
                open={openSettingsDrawer}
                toggleDrawer={toggleDrawer}
                setValue={goTo}
            />
        </Container>
    );
}

export {
    getTabId,
    getTabSlug,
}

export default withPather()(Settings);